// Copyright 2024 praveen.konathala
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { t } from 'i18next';
import AppIcons from 'src/utils/image';
import {
  formatAsPrice,
  formatPercentage,
  formatToShortNumber,
  formatWithCommas,
} from 'src/utils/Utils';

// config.ts
export const AppConfig = {
  apiUrl: process.env.REACT_APP_API_URL || 'defaultApiUrl',
  analyticsKey: process.env.REACT_APP_ANALYTICS_KEY || 'defaultAnalyticsKey',
  brands_db_url: process.env.REACT_APP_FIREBASE_CONFIG_BRANDS || '',
  locations_db_url: process.env.REACT_APP_FIREBASE_CONFIG_LOCATIONS || '',
  servicer_app_db_url: process.env.REACT_APP_FIREBASE_CONFIG_SERVICER_APP || '',
  user_db_url: process.env.REACT_APP_FIREBASE_CONFIG_USERS || '',
  firebaseApp: JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG || '{}') || {},
  zoomShopsFirebaseApp: JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG_ZOOM_SHOPS_OBJECT|| '{}') || {},
  env: process.env.ENVIRONMENT_NAME || 'dev',
  servicerAppUrl: process.env.REACT_APP_SERVICER_APP_URL || {},
  nmsdash_db_url: process.env.REACT_APP_FIREBASE_CONFIG_NMS_DASH || '',
  zoom_shops_db_url: process.env.REACT_APP_FIREBASE_CONFIG_ZOOM_SHOPS || '',
  clientTypeList: [
    { id: 'Brand', name: 'Organizations' },
    { id: 'Location', name: 'Locations' },
    {
      id: 'NMS',
      name: 'Network Monitoring',
      // name: 'NMS Dash App',
    },
  ],
  profileMenuList: [{ id: 'logout', name: 'LogOut' }],
  allDay: {
    name: 'All Day',
    from: '00:00',
    to: '24:00',
  },
  businessHours: {
    name: 'Business Day',
    from: '09:00',
    to: '22:00',
  },
  transactions: [
    'revenue',
    'transactions',
    'revenue_per_tx',
    'unit_per_tx',
    'tax_collected',
    'prodrevenue',
    'prodtransactions',
    'total_units',
  ],
  interActions: [
    'sessions',
    'transactions',
    'conversion',
    'new_customers',
    'returning_customers',
    'abandonment',
    'timeofday',
    'timeofweek',
    'pagenavigation',
  ],
  netOps: ['availability', 'misvend_sku', 'misvend_sales', 'dispatch_sales'],
  stores: [],
  storeStatus: ['Down', 'Exception', 'Maintenance', 'Online', 'Removed'],
  inventory: [],
  ticketType:['Dispatch', 'Research', 'Escalation'],
  ticketPriority:['1', '2', '3', '4'],
  admin: [
    'Organization Users',
    'Organization Roles',
    'Location Users',
    'Location Roles',
    'ServicerApp Users',
    'ServicerApp Roles',
  ],
  reports: [
    'Transactions',
    'Transaction Details',
    'Sales',
    'Inventory Events',
    'Inventory Snapshot',
    'Warehouse Inventory',
    'Network Availability',
    'Sessions Summary',
    'Marketing Survey',
  ],
  reportGroupBy: {
    Transactions: ['Transactions'],
    'Transaction Details': [],
    Sales: ['Products', 'Stores'],
    'Inventory Events': [
      'Transfer Out All',
      'Inventory Count Technician',
      'Sales Transaction',
      'Replenishment',
    ],
    'Inventory Snapshot': [],
    'Warehouse Inventory': [],
    'Network Availability': [],
    'Sessions Summary': [],
    'Marketing Survey': [],
  },
  reportGroupById: {
    Transactions: 'transactions',
    Products: 'product',
    Stores: 'store',
    'Transfer Out All': 'TRANSFER_OUT_ALL',
    'Inventory Count Technician': 'INVENTORY_COUNT_TECHNICIAN',
    'Sales Transaction': 'SALES_TRANSACTION',
    Replenishment: 'REPLENISHMENT',
  },
  reportAggregationById: {
    Month: 'monthly',
    '': '',
    undefined: '',
  },
  reportAggregation: {
    Transactions: [],
    'Transaction Details': [],
    Sales: ['Month'],
    'Inventory Events': [],
    'Inventory Snapshot': [],
    'Warehouse Inventory': [],
    'Network Availability': ['Month'],
    'Sessions Summary': ['Month'],
    'Marketing Survey': [],
  },
  adminTile: {
    icon: AppIcons.cog,
    labelKey: 'sidemenu.admin',
    alt: 'Admin',
    report: 'admin',
    tile: '',
  },
  adminMenus: [
    {
      icon: AppIcons.dollar,
      labelKey: 'sidemenu.transactions',
      alt: 'Transactions',
      report: 'transactions',
      tile: 'revenue',
    },
    {
      icon: AppIcons.graphUp,
      labelKey: 'sidemenu.interactions',
      alt: 'Interactions',
      report: 'interactions',
      tile: 'sessions',
    },
    {
      icon: AppIcons.netops,
      labelKey: 'sidemenu.netops',
      alt: 'Net Ops',
      report: 'netOps',
      tile: 'availability',
    },
    {
      icon: AppIcons.inventory,
      labelKey: 'sidemenu.inventory',
      alt: 'Inventory',
      report: 'inventory',
      tile: '',
    },
    {
      icon: AppIcons.store,
      labelKey: 'sidemenu.stores',
      alt: 'Stores',
      report: 'stores',
      tile: '',
    },
    {
      icon: AppIcons.person,
      labelKey: 'sidemenu.users',
      alt: 'Users',
      report: 'users',
      tile: '',
    },
    {
      icon: AppIcons.reports,
      labelKey: 'sidemenu.reports',
      alt: 'Reports',
      report: 'reports',
      tile: '',
    },
  ],
  nmsMenus: [
    {
      icon: AppIcons.reports,
      labelKey: 'sidemenu.NMS-Alert-Zendesk',
      alt: 'NMS Alert Zendesk',
      report: 'NMS-Alert-Zendesk',
      tile: '',
    },
    {
      icon: AppIcons.reports,
      labelKey: 'sidemenu.NMS-Worker-Logs',
      alt: 'NMS Worker Logs',
      report: 'NMS-Worker-Logs',
      tile: '',
    },
    {
      icon: AppIcons.reports,
      labelKey: 'sidemenu.Alert-Rules',
      alt: 'Alert Rules',
      report: 'Alert-Rules',
      tile: '',
    },
  ],
  internalUserMenus: [
    {
      icon: AppIcons.dollar,
      labelKey: 'sidemenu.transactions',
      alt: 'Transactions',
      report: 'transactions',
      tile: 'revenue',
    },
    {
      icon: AppIcons.graphUp,
      labelKey: 'sidemenu.interactions',
      alt: 'Interactions',
      report: 'interactions',
      tile: 'sessions',
    },
    {
      icon: AppIcons.netops,
      labelKey: 'sidemenu.netops',
      alt: 'Net Ops',
      report: 'netOps',
      tile: 'availability',
    },
    {
      icon: AppIcons.inventory,
      labelKey: 'sidemenu.inventory',
      alt: 'Inventory',
      report: 'inventory',
      tile: '',
    },
    {
      icon: AppIcons.store,
      labelKey: 'sidemenu.stores',
      alt: 'Stores',
      report: 'stores',
      tile: '',
    },
    {
      icon: AppIcons.reports,
      labelKey: 'sidemenu.reports',
      alt: 'Reports',
      report: 'reports',
      tile: '',
    },
  ],
  externalUserMenus: [
    {
      icon: AppIcons.dollar,
      labelKey: 'sidemenu.transactions',
      alt: 'Transactions',
      report: 'transactions',
      tile: 'revenue',
    },
    {
      icon: AppIcons.graphUp,
      labelKey: 'sidemenu.interactions',
      alt: 'Interactions',
      report: 'interactions',
      tile: 'sessions',
    },
    {
      icon: AppIcons.netops,
      labelKey: 'sidemenu.netops',
      alt: 'Net Ops',
      report: 'netOps',
      tile: 'availability',
    },
    {
      icon: AppIcons.inventory,
      labelKey: 'sidemenu.inventory',
      alt: 'Inventory',
      report: 'inventory',
      tile: '',
    },
    {
      icon: AppIcons.store,
      labelKey: 'sidemenu.stores',
      alt: 'Stores',
      report: 'stores',
      tile: '',
    },
    {
      icon: AppIcons.reports,
      labelKey: 'sidemenu.reports',
      alt: 'Reports',
      report: 'reports',
      tile: '',
    },
  ],
  tileMapping: {
    transactions: {
      revenue: {
        title_key: 'Revenue',
        sum_key: 'revenue_sum',
        graphType: 'line',
        toolTip: 'Revenue',
        showCurrency: true,
        value: (value: string | number) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
        header: 'Date',
        format: (value: string | number) => formatAsPrice(value),
        yFormat: (value: string | number) => `$${formatToShortNumber(value)}`,
        yTooltipFormat: (value: string | number) => formatWithCommas(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      transactions: {
        title_key: 'Transactions',
        sum_key: 'transactions_sum',
        graphType: 'line',
        toolTip: 'Transactions',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      revenue_per_tx: {
        title_key: 'Revenue per Tx',
        sum_key: 'revenue_per_tx_avg',
        graphType: 'line',
        toolTip: 'Revenue per Tx',
        showCurrency: true,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatAsPrice(value),
        yFormat: (value: string | number) => `$${formatToShortNumber(value)}`,
        yTooltipFormat: (value: string | number) => formatWithCommas(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      unit_per_tx: {
        title_key: 'Unit per Tx',
        sum_key: 'unit_per_tx_avg',
        graphType: 'line',
        toolTip: 'Unit per Tx',
        showCurrency: true,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatToShortNumber(value),
        yFormat: (value: string | number) => `${formatToShortNumber(value)}`,
        yTooltipFormat: (value: string | number) => formatWithCommas(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      tax_collected: {
        title_key: 'Tax Collected',
        sum_key: 'tax_collected_sum',
        graphType: 'line',
        toolTip: 'Tax Collected',
        showCurrency: true,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatAsPrice(value),
        yFormat: (value: string | number) => `$${formatToShortNumber(value)}`,
        yTooltipFormat: (value: string | number) => formatWithCommas(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      prodrevenue: {
        title_key: 'Revenue by SKU',
        sum_key: 'prodrevenue_sum',
        graphType: 'bar',
        toolTip: 'Revenue by SKU',
        showCurrency: false,
        value: (value: string | number) => value.toString(),
        header: 'Product Id',
        format: (value: string | number) => formatAsPrice(value),
        yFormat: (value: string | number) => `$${formatToShortNumber(value)}`,
        yTooltipFormat: (value: string | number, sum: number) =>
          formatPercentage(value, sum),
        xFormat: (value: string | number) => value,
        resultKey: 'results',
      },
      prodtransactions: {
        title_key: 'Transactions by SKU',
        sum_key: 'prodrevenue_sum',
        graphType: 'bar',
        toolTip: 'Transactions by SKU',
        showCurrency: false,
        value: (value: string | number) => value.toString(),
        header: 'Product Id',
        format: (value: string | number) => formatAsPrice(value),
        yFormat: (value: string | number) => `$${formatToShortNumber(value)}`,
        yTooltipFormat: (value: string | number, sum: number) =>
          formatPercentage(value, sum),
        xFormat: (value: string | number) => value,
        resultKey: 'results',
      },
      total_units: {
        title_key: 'Total Units',
        sum_key: 'total_units_sum',
        graphType: 'line',
        toolTip: 'Total Units',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },

      summary: ['revenue_per_tx', 'transactions', 'revenue', 'tax_collected'],
      summaryTitle: 'Summary',
    },
    interactions: {
      sessions: {
        title_key: 'Sessions',
        sum_key: 'sessions_sum',
        graphType: 'line',
        toolTip: 'Sessions',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      transactions: {
        title_key: 'Transactions',
        sum_key: 'transactions_sum',
        graphType: 'line',
        toolTip: 'Transactions',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        format: (value: string | number) => formatWithCommas(value),
        header: 'Date',
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      conversion: {
        title_key: 'Conversion',
        sum_key: 'conversion_sum',
        graphType: 'line',
        toolTip: 'Conversion',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        format: (value: string | number) => `${value}%`,
        header: 'Date',
        yFormat: (value: string | number) => `${value}%`,
        yTooltipFormat: (value: string | number) => `${value}%`,
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      new_customers: {
        title_key: 'New Customers',
        sum_key: 'new_customers_sum',
        graphType: 'line',
        toolTip: 'New Customers',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        format: (value: string | number) => formatWithCommas(value),
        header: 'Date',
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      returning_customers: {
        title_key: 'Returning Customers',
        sum_key: 'returning_customers_sum',
        graphType: 'line',
        toolTip: 'Returning Customers',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        //value: (value: string | number) => formatWithCommas(value),
        format: (value: string | number) => formatWithCommas(value),
        header: 'Date',
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      abandonment: {
        title_key: 'Abandonment',
        sum_key: 'abandonment_sum',
        graphType: 'line',
        toolTip: 'Abandonment',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        format: (value: string | number) => `${value}%`,
        header: 'Date',
        yFormat: (value: string | number) => `${value}%`,
        yTooltipFormat: (value: string | number) => `${value}%`,
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      timeofday: {
        title_key: 'Time of Day',
        sum_key: 'timeofday_sum',
        graphType: 'bar',
        toolTip: 'Time of Day',
        showCurrency: false,
        value: (value: string | number) => value.toLocaleString(),
        format: (value: string | number) => `${value}`,
        header: 'Metric',
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => `${value}%`,
        xFormat: (value: string | number) => {
          const startHour = Number(value);
          const startTime = `${startHour.toString().padStart(2, '0')}:00`;
          const endTime = `${startHour.toString().padStart(2, '0')}:59`;
          return `${startTime} - ${endTime}`;
        },
        resultKey: 'results',
      },
      timeofweek: {
        title_key: 'Time of Week',
        sum_key: 'timeofweek_sum',
        graphType: 'bar',
        toolTip: 'Time of Week',
        showCurrency: false,
        format: (value: string | number) => `${value}`,
        value: (value: string | number) => value.toLocaleString(),
        header: 'Metric',
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => `${value}%`,
        xFormat: (value: string | number) => value.toLocaleString(),
        resultKey: 'results',
      },
      pagenavigation: {
        title_key: 'User Journey Funnel',
        sum_key: 'user Journey',
        graphType: 'funnel',
        toolTip: 'User Journey Funnel',
        showCurrency: false,
        format: (value: string | number) => `${value}`,
        value: (value: string | number) => `${value}`,
        header: 'Metric',
        yFormat: (value: string | number) => `${value}%`,
        yTooltipFormat: (value: string | number) => `${value}%`,
        xFormat: (value: string | number) => `${value}%`,
        resultKey: 'data',
      },
      summary: [],
      summaryTitle: 'Traffic',
    },
    netOps: {
      availability: {
        title_key: 'Availability',
        sum_key: 'availability_avg',
        graphType: 'line',
        toolTip: 'Availability',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      misvend_sku: {
        title_key: 'Misvend per SKU',
        sum_key: 'misvend_sum',
        graphType: 'bar',
        toolTip: 'Misvend per SKU',
        showCurrency: false,
        value: (value: string | number) => value,
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        //yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number, sum: number) =>
          formatPercentage(value, sum),
        xFormat: (value: string | number) => value,
        resultKey: 'results',
      },
      misvend_sales: {
        title_key: 'Misvend per Sales',
        sum_key: 'misvend_sales_avg',
        graphType: 'line',
        toolTip: 'Misvend per Sales',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      dispatch_sales: {
        title_key: 'Dispatch per Sales',
        sum_key: 'dispatch_sales_avg',
        graphType: 'line',
        toolTip: 'Dispatch per Sales',
        showCurrency: false,
        value: (value: string | number) => new Date(value).toLocaleDateString(),
        header: 'Date',
        format: (value: string | number) => formatWithCommas(value),
        yFormat: (value: string | number) => formatToShortNumber(value),
        yTooltipFormat: (value: string | number) => formatToShortNumber(value),
        xFormat: (value: string | number, includeYear = true) =>
          new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(includeYear && { year: 'numeric' }),
          }),
        resultKey: 'results',
      },
      summary: [
        'availability',
        'misvend_sku',
        'misvend_sales',
        'dispatch_sales',
      ],
      summaryTitle: 'Summary',
    },
  },
  summaryMapping: {
    revenue_per_tx: 'Average Sales Price',
    transactions: 'Transactions',
    revenue: 'Revenue',
    tax_collected: 'Tax Collected',
    availability: 'Availability',
    misvend_sku: 'MisVend Total',
    misvend_sales: 'MisVend per Sales',
    dispatch_sales: 'Dispatch per Sales',
  },
  summaryMappingFormat: {
    revenue_per_tx: (value: string | number) => `${formatAsPrice(value)}`,
    transactions: (value: string | number) => `${formatWithCommas(value)}`,
    revenue: (value: string | number) => `${formatAsPrice(value)}`,
    tax_collected: (value: string | number) => `${formatAsPrice(value)}`,
    availability: (value: string | number) => `${value}%`,
    misvend_sku: (value: string | number) => `${formatWithCommas(value)}`,
    misvend_sales: (value: string | number) => `${formatWithCommas(value)}`,
    dispatch_sales: (value: string | number) => `${formatWithCommas(value)}`,
  },
  tooltipMapping: {
    transactions: `Provides key transactions metrics of stores across the network.`,
    interactions: `Provides key interaction metrics of stores across the network`,
    netOps: `Provides key performance metrics of stores across the network. Current week's metrics are not available as they are calculated at the end of each week.`,
  },
  reportMapping: {
    inventory: `Current product inventory on-hand and in-transit for each store. Data is refreshed once every 4 hours.`,
    Transactions:
      'Transaction details including customer information for the specified date range. \nGrouping available: Per transaction (Default), per transaction per product.',
    'Transaction Details': `Transaction details including per product information for the specified date range.`,
    Sales: `Sales information for each product within the specified date range.\nSales information can be viewed only on storeLocal timestamp for now.\nAggregation available: Daily (Default), or Monthly.\nGrouping available: Per store per product (Default), or per store, or per product.`,
    'Inventory Events': `Inventory changes within a store for the specified date range.`,
    'Inventory Snapshot': `Previous EOD snapshot of product inventory on-hand and in-transit for each store for the specified date range.\nNOTE: Pending inventory orders remain in-transit for a maximum of 14 days.',
    "/reports/network-availability": "Periodic summary of network availability for the specified date range.\nNOTE: Network availability is calculated iteratively, therefore recent data is subject to change.\nCurrent week's metrics are not available as they are calculated at the end of each week.\nAggregation available: Daily (Default), or Monthly.`,
    'Warehouse Inventory': `Returns an EOD snapshot of the product inventory on-hand & on-order for each warehouse for the specified date range.`,
    'Warehouse Inventory details': {
      list: {
        'On Order': {
          message: 'End of day inventory on order inbound to the warehouse.',
        },
        'On Hand': {
          color: '#ffa31a',
          message: 'End of day total inventory on-hand at the warehouse.',
        },
        'In Transit': {
          color: '#ff4d4d',
          message:
            'End of day inventory in transit outbound from the warehouse.',
        },
      },
    },
    'Network Availability': `Periodic summary of network availability for the specified date range.\nNOTE: Network availability is calculated iteratively, therefore recent data is subject to change.\nCurrent week's metrics are not available as they are calculated at the end of each week.\nAggregation available: Daily (Default), or Monthly.`,
    'Sessions Summary': `Summary of sessions and transactions created when a user interacts with a store.\nAggregation available: Daily (Default), or Monthly.`,
    'Marketing Survey': `Returns marketing opt-in during the selected period.`,
  },
};
